import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Drawer } from "../../../components/Modals";
import { useIntegrationStatus } from "../../../hooks/useIntegrationStatus";
import AsanaSettings from "./Asana/AsanaSettings";
import DevopsAzureSettings from "./DevopsAzure/DevopsAzureSettings";
import GithubSettings from "./Github/GithubSettings";
import JiraSettings from "./Jira/JiraSettings";
import MondaySettings from "./Monday/MondaySettings";
import SlackSettings from "./Slack/SlackSettings";

import { IntegrationGuide } from "../../../features/OnboardingGuide/InegrationGuide/IntegrationGuide";
import { ActiveIntegration, getIntegrationType, integrationData } from "./Integrations.types";

const Integrations = () => {
  const [activeIntegration, setActiveIntegration] = useState<ActiveIntegration | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { isIntegrationActive } = useIntegrationStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const jiraCardRef = useRef<HTMLDivElement>(null);
  const asanaCardRef = useRef<HTMLDivElement>(null);
  const azureCardRef = useRef<HTMLDivElement>(null);
  const mondayCardRef = useRef<HTMLDivElement>(null);
  const githubCardRef = useRef<HTMLDivElement>(null);
  const slackCardRef = useRef<HTMLDivElement>(null);
  const [handleActivationCallback, setHandleActivationCallback] = useState<(()=>void) | null>(null); 
 
  const handleIntegrationClick = (integration: ActiveIntegration, callback?: () => void) => {

    setActiveIntegration(integration);
    setShowModal(!showModal);

    // callback is a function that will be called after the modal is closed
    // Introduced for closing the Guide after the integration is activated
    
    if (callback) setHandleActivationCallback(()=>callback);
  };

  const handleClose = () => {
    setShowModal(!showModal);
  };



  const getScaledImages = (id: ActiveIntegration) => {
    switch (id) {
      case ActiveIntegration.AZURE:
        return "scale(0.65)"
      case ActiveIntegration.ASANA:
        return "scale(0.85)";
      case ActiveIntegration.JIRA:
        return "scale(0.55)";
      case ActiveIntegration.MONDAY:
        return "scale(1)"
      case ActiveIntegration.GITHUB:
        return "scale(0.55)"
      case ActiveIntegration.SLACK:
        return "scale(0.6)"
      default:
        return "scale(0.8)";
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const open = params.get('open');
    if (open && !showModal) {
      if (open.toLowerCase() === 'slack') {
        setActiveIntegration(ActiveIntegration.SLACK);
        setShowModal(true);
        params.delete('open');
        navigate({ pathname: location.pathname, hash: location.hash, search: params.toString() }, { replace: true });
      }
    }
  }, [location.search, location.pathname, location.hash, navigate, showModal]);

  return (
    <div className="row w-full h-full">
      <div className="mx-auto w-full px-8">
        <div
          className="flex flex-col flex-1 gap-1 border-b dark:border-gray-700 border-gray-200 sticky top-0 pt-8 pb-6 bg-light-surfacePrimary dark:bg-tertiary-dark z-20"
          data-onboarding-id="integration-header"
        >
          <h5 className="text-xl font-bold text-gray-900 dark:text-white font-['Inter']">
            Integrations
          </h5>
          <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 font-['Inter']">
            Import projects from your favorite tools and supercharge your
            project management experience.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-8 px-2 integrationGrid">
          {integrationData.map((integration) => {
            const isActive = isIntegrationActive(
              getIntegrationType(integration.id),
            );

            return (
              <div
                key={integration.id}
                className={`    
                  bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/20 dark:to-gray-700/20
                  border border-gray-200 dark:border-gray-700 
                  rounded-xl  cursor-pointer 
                  transform transition-all duration-300 ease-in-out
                  hover:scale-105 hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-gray-900/50
                  group relative overflow-hidden
                  backdrop-blur-sm
                  flex flex-col h-[350px]
                  integration_${getIntegrationType(integration.id)}
                `}
                ref={
                  integration.id === ActiveIntegration.JIRA
                    ? jiraCardRef
                    : integration.id === ActiveIntegration.ASANA
                      ? asanaCardRef
                      : integration.id === ActiveIntegration.AZURE
                        ? azureCardRef
                        : integration.id === ActiveIntegration.MONDAY
                          ? mondayCardRef
                          : integration.id === ActiveIntegration.GITHUB
                            ? githubCardRef
                            : integration.id === ActiveIntegration.SLACK
                              ? slackCardRef
                              : undefined
                }
                onClick={() => handleIntegrationClick(integration.id)}
              >
                {/* Animated background gradient */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-800 opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
                />

                {/* Logo section - Top 50% */}
                <div className="flex-1 min-h-[50%] max-h-[50%] flex items-center justify-center relative z-10 bg-white">
                  <img
                    alt={integration.name}
                    className={`bg-white w-full h-full object-contain`}
                    src={integration.logo}
                    style={{
                      transform: getScaledImages(integration.id),
                    }}
                  />
                </div>

                {/* Content section - Bottom 50% */}
                <div className="flex-1 min-h-[50%] max-h-[50%] flex flex-col px-4 py-2 justify-center space-y-2 relative z-10">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white font-['Inter'] group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors text-left">
                      {integration.name}
                    </h3>
                    {isActive && (
                      <div className="flex items-center gap-1">
                        <div
                          title="Active Integration"
                          className=" bg-green-400 rounded-full  animate-pulse shadow-sm"
                        />
                        <span className="text-green-400  px-2 py-1  border border-green-400 rounded-full text-xs">
                          Active
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-['Inter'] leading-relaxed text-left">
                    {integration.description}
                  </p>

                  {/* Features list */}
                  <div className="flex flex-wrap justify-start gap-1">
                    {integration.features.map((feature, index) => (
                      <div
                        key={index}
                        className="text-xs text-gray-500 dark:text-gray-500 font-['Inter'] bg-white/50 dark:bg-gray-800/50 rounded-full px-2 py-1"
                      >
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status indicator - only show for active integrations */}
              </div>
            );
          })}
        </div>

        {/* <IntegrationGuideModal isOpen={true} onClose={() => { }} onSelectIntegration={handleIntegrationClick} /> */}
        {/* <IntegrationsOnboarding
          refsByKey={{
            jira: jiraCardRef,
            asana: asanaCardRef,
            azure: azureCardRef,
            monday: mondayCardRef,
            github: githubCardRef,
            slack: slackCardRef
          }}
          isModalOpen={showModal}
        /> */}

        <IntegrationGuide handleIntegrationClick={handleIntegrationClick}/>
      </div>

      <Drawer
        isOpen={showModal}
        onClose={handleClose}
        position="right"
        size="42"
        minsize="42"
      >
        {activeIntegration === ActiveIntegration.JIRA ? (
          <JiraSettings handleClose={handleClose} handleActivationCallback={handleActivationCallback || undefined} />
        ) : null}

        {activeIntegration === ActiveIntegration.ASANA ? (
          <AsanaSettings handleClose={handleClose} handleActivationCallback={handleActivationCallback || undefined} />
        ) : null}

        {activeIntegration === ActiveIntegration.AZURE ? (
          <DevopsAzureSettings handleClose={handleClose} handleActivationCallback={handleActivationCallback || undefined} />
        ) : null}

        {activeIntegration === ActiveIntegration.MONDAY ? (
          <MondaySettings handleClose={handleClose} handleActivationCallback={handleActivationCallback || undefined} />
        ) : null}

        {activeIntegration === ActiveIntegration.GITHUB ? (
          <GithubSettings handleClose={handleClose} handleActivationCallback={handleActivationCallback || undefined} />
        ) : null}

        {activeIntegration === ActiveIntegration.SLACK ? (
          <SlackSettings handleClose={handleClose} handleActivationCallback={handleActivationCallback || undefined} />
        ) : null}
      </Drawer>
    </div>
  );
};

export default Integrations;